import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { AuthGuard, RoleGuard } from '@qcdoc/shared/shared/guards/index.ts';
import { AppRoles } from '@qcdoc/mock-runtime/contracts/enums.ts';
import { AUTH_MODE } from '@qcdoc/shared/hooks/common/useAuth.ts';
import PackagingGuard from '@qcdoc/shared/shared/guards/PackagingGuard.tsx';
import { notFoundRoute, shellRoutes, withSuspense } from '@qcdoc/shared/app/routes.shared.tsx';

const VendorLayout = lazy(() => import('../features/vendor/layout/VendorLayout.tsx'));
const VendorHomePage = lazy(() => import('../features/vendor/VendorHomePage.tsx'));
const VendorDashboardPage = lazy(() => import('../containers/vendor/dashboard/VendorDashboardContainer.tsx'));
const VendorLoginPage = lazy(() => import('../features/vendor/VendorLoginPage.tsx'));
const VendorLoginCallback = lazy(() => import('../features/vendor/VendorLoginCallback.tsx'));
const VendorSignupPage = lazy(() => import('../features/vendor/VendorSignupPage.tsx'));
const VendorCOAListPage = lazy(() => import('../containers/vendor/coa/VendorCOAListContainer.tsx'));
const VendorCOADetailPage = lazy(() => import('../containers/vendor/coa/VendorCOADetailContainer.tsx'));
const VendorCreateCOAPage = lazy(() => import('../containers/vendor/coa/VendorCreateCOAContainer.tsx'));
const VendorCOAAttachments = lazy(() => import('../containers/vendor/coa/VendorCOAAttachmentsContainer.tsx'));
const VendorVQCAListPage = lazy(() => import('../containers/vendor/vqca/VendorVQCAListContainer.tsx'));
const VendorVQCADetailPage = lazy(() => import('../containers/vendor/vqca/VendorVQCADetailContainer.tsx'));
const VendorVQCASignPage = lazy(() => import('../containers/vendor/vqca/VendorVQCASignContainer.tsx'));
const VendorPendingSignPage = lazy(() => import('../containers/vendor/vqca/VendorPendingSignContainer.tsx'));
const VendorPackageListPage = lazy(() => import('../containers/vendor/packaging/VendorPackageListContainer.tsx'));
const VendorPackageDetailPage = lazy(() => import('../containers/vendor/packaging/VendorPackageDetailContainer.tsx'));
const VendorCreatePackagePage = lazy(() => import('../containers/vendor/packaging/VendorCreatePackageContainer.tsx'));
const ReleaseNotesPage = lazy(() => import('@qcdoc/shared/features/release-notes/ReleaseNotesPage.tsx'));

const VENDOR_PORTAL_ROLES = [AppRoles.Vendor, AppRoles.VendorAdmin, 'SuperUser'];

export const vendorRoutes: RouteObject[] = [
      // ── Vendor Portal sign-in (anonymous, mirrors legacy Signin.aspx) ──
      { path: '/vendor/login', element: withSuspense(<VendorLoginPage />) },
      { path: '/vendor/signup', element: withSuspense(<VendorSignupPage />) },

      ...(AUTH_MODE === 'entra'
        ? [{ path: '/vendor/login/callback', element: withSuspense(<VendorLoginCallback />) }]
        : []),

      // ── Vendor Portal Routes (separate layout, mirrors legacy Web.sitemap) ──
      {
        path: '/vendor',
        element: (
          <AuthGuard redirectTo="/vendor/login">
            <RoleGuard requiredRoles={VENDOR_PORTAL_ROLES} showForbidden>
              {withSuspense(<VendorLayout />)}
            </RoleGuard>
          </AuthGuard>
        ),
        children: [
          { index: true, element: withSuspense(<VendorDashboardPage />) },
          { path: 'release-notes', element: withSuspense(<ReleaseNotesPage />) },

          // VQCA
          { path: 'vqca', element: withSuspense(<VendorHomePage />) },
          { path: 'vqca/pending', element: withSuspense(<VendorPendingSignPage />) },
          { path: 'vqca/my', element: withSuspense(<VendorVQCAListPage />) },
          { path: 'vqca/:id', element: withSuspense(<VendorVQCADetailPage />) },
          { path: 'vqca/:id/sign', element: withSuspense(<VendorVQCASignPage />) },

          // COA
          { path: 'coa', element: withSuspense(<VendorHomePage />) },
          { path: 'coa/new', element: withSuspense(<VendorCreateCOAPage />) },
          { path: 'coa/my', element: withSuspense(<VendorCOAListPage />) },
          { path: 'coa/:id', element: withSuspense(<VendorCOADetailPage />) },
          { path: 'coa/:id/attachments', element: withSuspense(<VendorCOAAttachments />) },

          // Packaging
          { path: 'packaging', element: <PackagingGuard access="vendor" />, children: [
            { index: true, element: withSuspense(<VendorHomePage />) },
            { path: 'new', element: withSuspense(<VendorCreatePackagePage />) },
            { path: 'my', element: withSuspense(<VendorPackageListPage />) },
            { path: ':id', element: withSuspense(<VendorPackageDetailPage />) },
          ] },
        ],
      },

];

export const routes = shellRoutes([
  { path: '/', element: <Navigate to="/vendor" replace /> },
  { path: '/login', element: <Navigate to="/vendor/login" replace /> },
  { path: '/signup', element: <Navigate to="/vendor/signup" replace /> },
  ...vendorRoutes,
  notFoundRoute,
]);
